/* global Office, Word */

/**
 * Word selection tracker.
 *
 * `document.getSelection()` only sees the live selection at the moment a
 * `Word.run()` executes. When the user highlights text in Word, then
 * clicks the task pane to send a message, Word collapses the selection
 * before the tool fires — so selection-dependent tools (`read_selection`,
 * `replace_selection`, `add_comment`, etc.) see nothing.
 *
 * Fix: subscribe to the Common-API `DocumentSelectionChanged` event and,
 * on every non-empty selection, drop a hidden bookmark
 * (`magnusLastSelection`) on the range. Bookmarks survive across
 * `Word.run` calls and focus shifts, so executors can fall back to the
 * bookmark when the live selection is empty.
 *
 * We use the Common API (`Office.context.document.addHandlerAsync`)
 * instead of `Word.Document.onSelectionChange` because the latter isn't
 * in the stable @types/office-js surface and may be preview-only on
 * older Word builds.
 *
 * Idempotent: subscribing more than once is harmless because we guard
 * with a module-level flag.
 */

export const MAGNUS_SELECTION_BOOKMARK = "magnusLastSelection";

let trackerStarted = false;

export async function startWordSelectionTracking(): Promise<void> {
  if (trackerStarted) {
    return;
  }
  if (typeof Office === "undefined" || !Office.context?.document) {
    return;
  }
  trackerStarted = true;

  try {
    await new Promise<void>((resolve, reject) => {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handleSelectionChange,
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            reject(
              new Error(asyncResult.error?.message ?? "addHandlerAsync failed")
            );
          } else {
            resolve();
          }
        }
      );
    });
  } catch (err) {
    trackerStarted = false;
    console.warn(
      "[Magnus Word] Failed to register selection-change handler",
      err
    );
  }
}

async function handleSelectionChange(): Promise<void> {
  if (typeof Word === "undefined" || !Word.run) {
    return;
  }
  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text,isEmpty");
      await context.sync();
      if (selection.isEmpty || !selection.text) {
        return;
      }
      // Re-bookmarking the same range each time is cheap and overwrites
      // the previous bookmark — there's only one `magnusLastSelection`
      // at a time.
      selection.insertBookmark(MAGNUS_SELECTION_BOOKMARK);
      await context.sync();
    });
  } catch (err) {
    console.warn("[Magnus Word] selection-change handler failed", err);
  }
}

/**
 * Resolve the "effective" selection for a tool call:
 *   - Live selection if non-empty.
 *   - Bookmarked range otherwise.
 *   - null when neither is available.
 *
 * Returns a Range plus a flag identifying which source it came from
 * (some callers may want to behave differently — e.g. `replace_selection`
 * delivers feedback like "replaced your last highlighted text").
 */
export async function getEffectiveSelectionRange(
  ctx: Word.RequestContext
): Promise<{ range: Word.Range; source: "live" | "bookmark" } | null> {
  const live = ctx.document.getSelection();
  live.load("text,isEmpty");
  await ctx.sync();
  if (!live.isEmpty && live.text) {
    return { range: live, source: "live" };
  }
  const bookmarked = ctx.document.getBookmarkRangeOrNullObject(
    MAGNUS_SELECTION_BOOKMARK
  );
  bookmarked.load("text,isNullObject");
  await ctx.sync();
  if (!bookmarked.isNullObject && bookmarked.text) {
    return { range: bookmarked, source: "bookmark" };
  }
  return null;
}
